import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IPersonaProps } from '@fluentui/react';
import {
  Divider,
  Field,
  FluentProvider,
  Input,
  Textarea,
  webLightTheme,
} from '@fluentui/react-components';
import React from 'react';
import { Controller, FieldValues, UseFormMethods } from 'react-hook-form';
import moment from 'moment';
import { dividerStyles } from '../../../app/common/styles/CommonStyleObjects';
import ErrorHandlingPeoplePicker from '../../common/formFields/ErrorHandlingPeoplePicker';
import UtcDatePicker from '../../common/utcDatePicker/UtcDatePicker';
import { IEntityTerminationTask } from '../types';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';
import {
  onlyWhenChanged,
  parseCalendarDate,
  validateNotBeforeMinDate,
} from '../utils/taskCompletionDates';

interface IEntityTerminationTaskEditFormProps {
  form: UseFormMethods<FieldValues>;
  selectedTask?: IEntityTerminationTask;
  isOwner: boolean;
  isAssignee: boolean;
  isNewTask: boolean;
  allowMultipleAssignees?: boolean;
  actionItemStartDate?: Date | string | null;
  isDescriptionReadOnly?: boolean;
}

const EntityTerminationTaskEditForm = (props: IEntityTerminationTaskEditFormProps): JSX.Element => {
  const {
    form,
    selectedTask: selectedActionItem,
    isOwner,
    isNewTask,
    allowMultipleAssignees,
    isAssignee,
    actionItemStartDate,
    isDescriptionReadOnly = false,
  } = props;
  const styles = dividerStyles();
  // Use local calendar end-of-day so maxDate aligns with the user's "today"
  const today = moment().endOf('day').toDate();
  // Task dates cannot be earlier than the parent Action Item's Date Started.
  const minTaskDate = parseCalendarDate(actionItemStartDate);
  const validateNotBeforeActionItemStart = onlyWhenChanged(
    selectedActionItem?.completedDate,
    validateNotBeforeMinDate(minTaskDate, 'Action Item Start Date', 'Completed Date'),
  );
  const {
    control,
    formState: { errors },
  } = form;

  const onResolveSuggestions = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return searchForUsers(filterText);
    }
    return [];
  };
  // Auto-generated tasks (e.g. Finance Incomplete) can have no owner; an unowned task
  // shouldn't lock its fields behind the owner/assignee gate.
  const isUnowned = !selectedActionItem?.owner;
  const canEditAdditionalTask = isOwner || isNewTask || isUnowned;
  const canModifyAssignees = allowMultipleAssignees
    ? isOwner || !!isAssignee || isNewTask || isUnowned
    : true;
  const canEditResponse = isOwner || isAssignee || isUnowned;
  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        {isDescriptionReadOnly ? (
          <Field label="Task Description">
            <Input
              id="description"
              value={selectedActionItem?.description ?? ''}
              disabled
              readOnly
            />
          </Field>
        ) : (
          <Controller
            control={control}
            name="description"
            defaultValue={selectedActionItem ? selectedActionItem.description : ''}
            rules={{
              required: 'Please enter task description.',
            }}
            render={({ onChange, onBlur, value, name, ref }) => (
              <Field
                label="Task Description"
                required
                validationMessage={errors.description?.message}
              >
                <Input
                  id="description"
                  value={value}
                  name={name}
                  ref={ref}
                  onBlur={onBlur}
                  onChange={(ev, data) => onChange(data.value)}
                />
              </Field>
            )}
          />
        )}
        <Controller
          name="owner"
          control={control}
          defaultValue={selectedActionItem?.owner ?? null}
          rules={{ required: 'Please add owner.' }}
          render={({ onChange, value }) => (
            <ErrorHandlingPeoplePicker
              controlName="owner"
              labelText="Owner"
              required
              itemLimit={1}
              selectedItems={value ? [value] : []}
              errorMessage={errors?.owner?.message}
              removeButtonAriaLabel="Remove Owner"
              onResolveSuggestions={onResolveSuggestions}
              onChange={(items: IServiceContact[]) =>
                onChange((items?.[0] as IServiceContact) ?? null)
              }
            />
          )}
        />
        <Controller
          control={control}
          name="comment"
          defaultValue={selectedActionItem ? selectedActionItem.comment : ''}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Additional Task" validationMessage={errors.comment?.message}>
              <Textarea
                id="comment"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                disabled={!canEditAdditionalTask}
                onChange={(ev, data) => onChange(data.value)}
              />
            </Field>
          )}
        />
        {allowMultipleAssignees ? (
          <Controller
            name="assignees"
            control={control}
            defaultValue={selectedActionItem?.assignees ?? []}
            rules={{ validate: (v) => (v && v.length > 0) || 'Please add at least one assignee.' }}
            render={({ onChange, value }) => (
              <ErrorHandlingPeoplePicker
                controlName="assignees"
                labelText="Assignees"
                required
                disabled={!canModifyAssignees}
                selectedItems={value ?? []}
                errorMessage={errors?.assignees?.message}
                removeButtonAriaLabel="Remove Assignee"
                onResolveSuggestions={onResolveSuggestions}
                onChange={(items: IServiceContact[]) => onChange(items ?? [])}
              />
            )}
          />
        ) : (
          <Controller
            name="assignee"
            control={control}
            defaultValue={selectedActionItem?.assignee ?? null}
            rules={{ required: 'Please add assignee.' }}
            render={({ onChange, value }) => (
              <ErrorHandlingPeoplePicker
                controlName="assignee"
                labelText="Assignee"
                required
                itemLimit={1}
                selectedItems={value ? [value] : []}
                errorMessage={errors?.assignee?.message}
                removeButtonAriaLabel="Remove Assignee"
                onResolveSuggestions={onResolveSuggestions}
                onChange={(items: IServiceContact[]) =>
                  onChange((items?.[0] as IServiceContact) ?? null)
                }
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="followUpFlagDate"
          defaultValue={selectedActionItem?.followUpFlagDate || null}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Follow-Up Flag Date" validationMessage={errors.followUpFlagDate?.message}>
              <UtcDatePicker
                label=""
                allowTextInput
                disableAutoFocus={false}
                value={value}
                textField={{
                  name: 'followUpFlagDate',
                }}
                onSelectDate={(utcDateString: string): void => {
                  onChange(utcDateString || null);
                }}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="completedDate"
          defaultValue={selectedActionItem?.completedDate || null}
          rules={{ validate: validateNotBeforeActionItemStart }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Completed Date" validationMessage={errors.completedDate?.message}>
              <UtcDatePicker
                label=""
                allowTextInput
                disableAutoFocus={false}
                value={value}
                minDate={minTaskDate}
                maxDate={today}
                textField={{
                  name: 'completedDate',
                }}
                onSelectDate={(utcDateString: string): void => {
                  onChange(utcDateString || null);
                }}
              />
            </Field>
          )}
        />
        <Divider className={styles.divider} />

        <Controller
          control={control}
          name="response"
          defaultValue=""
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Response">
              <Textarea
                id="response"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                disabled={!canEditResponse}
                onChange={(ev, textareaData) => onChange(textareaData.value)}
              />
            </Field>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default EntityTerminationTaskEditForm;
